import React, { useState, useEffect, useCallback } from 'react';
import { db, functions } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { STOCKS, STOCK_CATEGORIES } from '../../constants/stocks';
import StockCard from './StockCard';
import StockDetailModal from './StockDetailModal';
import StockTradeModal from './StockTradeModal';
import StockPortfolio from './StockPortfolio';
import { useToast } from '../ToastContext';

function StocksTab({ user, userData, onUserDataChange }) {
  const toast = useToast();
  const [innerTab, setInnerTab] = useState('market'); // 'market' or 'portfolio'
  const [prices, setPrices] = useState(null);
  const [holdings, setHoldings] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null); // for trade modal
  const [detailStock, setDetailStock] = useState(null); // for detail modal
  const [isProcessing, setIsProcessing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stockNews, setStockNews] = useState(null);

  // Fetch stock prices (polling, not real-time — avoids Firestore listener bugs on missing docs)
  const fetchPrices = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'stockPrices', 'current'));
      if (snap.exists()) {
        const data = snap.data();
        setPrices(data.prices);
        setLastUpdated(data.lastUpdated);
      }
    } catch (err) {
      console.warn('Failed to fetch stock prices:', err.message);
    }
  }, []);

  // Fetch user holdings
  const fetchHoldings = useCallback(async () => {
    if (!user) return;
    try {
      const snap = await getDoc(doc(db, 'stockHoldings', user.uid));
      if (snap.exists()) {
        setHoldings(snap.data().holdings || {});
      } else {
        setHoldings({});
      }
    } catch (err) {
      console.warn('Failed to fetch stock holdings:', err.message);
    }
  }, [user]);

  // Fetch stock news
  const fetchNews = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'stockNews', 'current'));
      if (snap.exists()) {
        setStockNews(snap.data().news || {});
      }
    } catch (err) {
      console.warn('Failed to fetch stock news:', err.message);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPrices();
    fetchHoldings();
    fetchNews();
  }, [fetchPrices, fetchHoldings, fetchNews]);

  // Poll prices — every 15s when update is overdue, every 60s otherwise
  useEffect(() => {
    function shouldPollFast() {
      if (!lastUpdated) return true;
      return Date.now() - lastUpdated >= 10 * 60 * 1000;
    }
    const interval = setInterval(fetchPrices, shouldPollFast() ? 15000 : 60000);
    return () => clearInterval(interval);
  }, [fetchPrices, lastUpdated]);

  // Try to trigger price update if no prices exist yet
  useEffect(() => {
    if (!prices && user) {
      const refreshPrices = httpsCallable(functions, 'updateStockPricesManual');
      refreshPrices()
        .then(() => { setTimeout(fetchPrices, 3000); })
        .catch(() => {}); // Best effort
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Try to trigger news generation if none exists
  useEffect(() => {
    if (!stockNews && user) {
      const generateNews = httpsCallable(functions, 'generateStockNewsManual');
      generateNews()
        .then(() => { setTimeout(fetchNews, 5000); })
        .catch(() => {}); // Best effort
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, stockNews]);

  async function handleBuy(stockId, shares, currency) {
    setIsProcessing(true);
    try {
      const buyStock = httpsCallable(functions, 'buyStock');
      const result = await buyStock({ stockId, shares, currency });
      const icon = result.data.currency === 'impostor' ? '🔴' : '🔵';
      toast.success(`Bought ${shares.toFixed(4)} shares for ${result.data.tokenCost.toFixed(2)} ${icon}`);
      await fetchHoldings();
      if (onUserDataChange) await onUserDataChange();
      setSelectedStock(null);
    } catch (err) {
      toast.error(err.message || 'Failed to buy stock');
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleSell(stockId, shares, currency) {
    setIsProcessing(true);
    try {
      const sellStock = httpsCallable(functions, 'sellStock');
      const result = await sellStock({ stockId, shares, currency });
      const icon = result.data.currency === 'impostor' ? '🔴' : '🔵';
      const profitStr = result.data.profit >= 0
        ? `+$${result.data.profit.toFixed(2)}`
        : `-$${Math.abs(result.data.profit).toFixed(2)}`;
      toast.success(`Sold ${shares.toFixed(4)} shares for ${result.data.tokenProceeds.toFixed(2)} ${icon} (${profitStr})`);
      await fetchHoldings();
      if (onUserDataChange) await onUserDataChange();
      setSelectedStock(null);
    } catch (err) {
      toast.error(err.message || 'Failed to sell stock');
    } finally {
      setIsProcessing(false);
    }
  }

  // Countdown timer — ticks every second
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Filter stocks
  const filteredStocks = STOCKS.filter(stock => {
    if (filter !== 'all' && stock.category !== filter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return stock.name.toLowerCase().includes(q) || stock.id.includes(q);
    }
    return true;
  });

  // Calculate countdown to next update (every 10 minutes from last update)
  const UPDATE_INTERVAL = 10 * 60 * 1000; // 10 minutes
  const nextUpdate = lastUpdated ? lastUpdated + UPDATE_INTERVAL : null;
  const secondsUntilUpdate = nextUpdate ? Math.max(0, Math.floor((nextUpdate - now) / 1000)) : null;
  const countdownMin = secondsUntilUpdate !== null ? Math.floor(secondsUntilUpdate / 60) : null;
  const countdownSec = secondsUntilUpdate !== null ? secondsUntilUpdate % 60 : null;

  return (
    <div className="stocks-tab">
      {/* Inner tab navigation */}
      <div className="stocks-inner-tabs">
        <button
          className={`stocks-inner-tab ${innerTab === 'market' ? 'active' : ''}`}
          onClick={() => setInnerTab('market')}
        >
          📊 Market
        </button>
        <button
          className={`stocks-inner-tab ${innerTab === 'portfolio' ? 'active' : ''}`}
          onClick={() => setInnerTab('portfolio')}
        >
          💼 Portfolio
        </button>
      </div>

      {innerTab === 'market' && (
        <div className="stocks-market">
          {/* Search and filter bar */}
          <div className="stocks-filter-bar">
            <input
              type="text"
              className="stocks-search"
              placeholder="Search stocks..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <select
              className="stocks-filter-select"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              {Object.entries(STOCK_CATEGORIES).map(([key, cat]) => (
                <option key={key} value={key}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Last updated + countdown */}
          {lastUpdated && (
            <div className="stocks-updated-info">
              {secondsUntilUpdate > 0 ? (
                <>Next update in <span className="stocks-countdown">{countdownMin}:{String(countdownSec).padStart(2, '0')}</span></>
              ) : (
                <>Updating prices...</>
              )}
              {secondsUntilUpdate <= 0 && now - lastUpdated > UPDATE_INTERVAL + 60000 && (
                <span className="stocks-stale-warning"> (may be stale)</span>
              )}
            </div>
          )}

          {/* Stock grid */}
          <div className="stocks-grid">
            {filteredStocks.map(stock => (
              <StockCard
                key={stock.id}
                stock={stock}
                priceData={prices?.[stock.ticker]}
                holding={holdings?.[stock.id]}
                onClick={() => setDetailStock(stock)}
              />
            ))}
          </div>

          {filteredStocks.length === 0 && (
            <div className="stocks-empty">No stocks match your filter.</div>
          )}
        </div>
      )}

      {innerTab === 'portfolio' && (
        <StockPortfolio
          holdings={holdings}
          prices={prices}
          onStockClick={stock => setDetailStock(stock)}
        />
      )}

      {/* Detail modal */}
      {detailStock && !selectedStock && (
        <StockDetailModal
          stock={detailStock}
          priceData={prices?.[detailStock.ticker]}
          holding={holdings?.[detailStock.id]}
          newsData={stockNews?.[detailStock.ticker]}
          onTrade={(stock) => { setSelectedStock(stock); setDetailStock(null); }}
          onClose={() => setDetailStock(null)}
        />
      )}

      {/* Trade modal */}
      {selectedStock && (
        <StockTradeModal
          stock={selectedStock}
          priceData={prices?.[selectedStock.ticker]}
          holding={holdings?.[selectedStock.id]}
          userData={userData}
          onBuy={handleBuy}
          onSell={handleSell}
          onClose={() => setSelectedStock(null)}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
}

export default StocksTab;
